import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { scrollPageToTop } from "../utils/scrollPageToTop";

/** Mount once inside BrowserRouter, above Routes, for all public/dashboard pages. */
export default function ScrollToTopOnNavigate() {
  const { key, pathname, search } = useLocation();

  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    let active = true;
    let raf = 0;
    let timeout = 0;

    const reset = () => {
      if (!active) return;

      // Preserve the application's existing reset helper.
      scrollPageToTop();

      // Force an immediate reset even when global CSS enables smooth scrolling.
      window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // Some layouts scroll a content area instead of the browser window.
      // Touch only the page's main scroll region, never arbitrary nested widgets.
      document
        .querySelectorAll<HTMLElement>(
          "main, [role='main'], [data-scroll-container], [data-scroll-restoration='reset']"
        )
        .forEach((element) => {
          if (element.scrollTop !== 0) element.scrollTop = 0;
        });
    };

    const scheduleReset = () => {
      if (!active) return;
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(reset);
    };

    const stop = () => {
      if (!active) return;
      active = false;
      observer.disconnect();
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("pointerdown", stop);
      window.removeEventListener("keydown", stop);
    };

    // The new route and its first browser layout pass.
    reset();
    scheduleReset();

    // Covers lazy rendering and async page sections during a short transition.
    const observer = new MutationObserver(scheduleReset);
    observer.observe(document.body, { childList: true, subtree: true });

    // Don't override a user's intentional scrolling or interaction.
    window.addEventListener("wheel", stop, { once: true, passive: true });
    window.addEventListener("touchstart", stop, { once: true, passive: true });
    window.addEventListener("pointerdown", stop, { once: true, passive: true });
    window.addEventListener("keydown", stop, { once: true });

    timeout = window.setTimeout(stop, 600);
    return stop;
  }, [key, pathname, search]);

  return null;
}
